import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { ArrowRight, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Whole House Soft Wash",
  "Driveway & Walkway",
  "Roof & Gutter Cleaning",
  "Front Porch Painting",
  "Deck Staining",
  "$380 Whole House Bundle",
  "Something Else",
];

const inputCls =
  "bg-fr-black border-white/20 rounded-none text-white placeholder:text-white/30 focus-visible:border-fr-blue focus-visible:ring-fr-blue h-12";

export const EstimateForm = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.service) {
      toast.error("Please fill in name, phone, email and service.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/estimates`, form);
      setDone(true);
      toast.success("Request sent! We'll reach out shortly.");
      setForm({ name: "", phone: "", email: "", address: "", service: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please call 843-297-5559.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="estimate" className="py-24 sm:py-32 bg-fr-black" data-testid="estimate-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16">
        <Reveal>
          <span className="fr-mono text-xs tracking-[0.3em] uppercase text-fr-blue-light">/ Free estimate</span>
          <h2 className="mt-5 font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none">
            Get your free quote today.
          </h2>
          <p className="mt-6 text-white/60 max-w-md leading-relaxed">
            Tell us about your project and we{"'"}ll get back to you fast with a no-obligation estimate.
            Prefer to talk? Call or text{" "}
            <a href="tel:8432975559" className="text-fr-blue-light font-semibold">843-297-5559</a>.
          </p>
          <div className="mt-10 border-t border-white/10 pt-8 space-y-4">
            {["Fast, reliable & professional service", "Satisfaction guaranteed on every job", "Serving all of Charleston, SC"].map((t) => (
              <div key={t} className="flex items-center gap-3 text-white/70">
                <CheckCircle2 size={18} className="text-fr-blue-light" /> {t}
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          {done ? (
            <div
              data-testid="estimate-success"
              className="bg-fr-surface border border-fr-blue/40 p-10 h-full flex flex-col items-start justify-center"
            >
              <CheckCircle2 size={48} className="text-fr-blue-light mb-6" />
              <h3 className="font-display font-bold text-3xl mb-3">Request received.</h3>
              <p className="text-white/60 mb-8">Thanks! A team member will call or text you shortly. Check your email for a confirmation.</p>
              <button data-testid="estimate-reset-btn" onClick={() => setDone(false)} className="btn-outline">
                Send another request
              </button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="estimate-form" className="bg-fr-surface border border-white/10 p-8 sm:p-10 space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label className="fr-mono text-xs tracking-widest uppercase text-white/60">Name *</Label>
                  <Input data-testid="input-name" value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
                </div>
                <div className="space-y-2">
                  <Label className="fr-mono text-xs tracking-widest uppercase text-white/60">Phone *</Label>
                  <Input data-testid="input-phone" value={form.phone} onChange={set("phone")} placeholder="843-000-0000" className={inputCls} />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="fr-mono text-xs tracking-widest uppercase text-white/60">Email *</Label>
                <Input data-testid="input-email" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputCls} />
              </div>
              <div className="space-y-2">
                <Label className="fr-mono text-xs tracking-widest uppercase text-white/60">Property Address</Label>
                <Input data-testid="input-address" value={form.address} onChange={set("address")} placeholder="Street, Charleston, SC" className={inputCls} />
              </div>
              <div className="space-y-2">
                <Label className="fr-mono text-xs tracking-widest uppercase text-white/60">Service *</Label>
                <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                  <SelectTrigger data-testid="select-service" className={`${inputCls} data-[placeholder]:text-white/30`}>
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent className="bg-fr-elevated border-white/15 text-white rounded-none">
                    {SERVICES.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`service-option-${s}`} className="focus:bg-fr-blue focus:text-white">
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="fr-mono text-xs tracking-widest uppercase text-white/60">Message</Label>
                <Textarea data-testid="input-message" value={form.message} onChange={set("message")} placeholder="Tell us about your project..." className={`${inputCls} h-28`} />
              </div>
              <button data-testid="estimate-submit-btn" type="submit" disabled={loading} className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60">
                {loading ? <><Loader2 className="animate-spin" size={18} /> Sending...</> : <>Request Free Estimate <ArrowRight size={18} /></>}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
